import type { Command } from "../document/commands.ts";
import { playersAbove } from "../document/band.ts";
import type { LayerDocument, LayerGroup, MapDocument, WorldDocument } from "../document/types.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { LayerGroups } from "./layer-groups.ts";
import { LayerMapView } from "./layer-map-view.ts";
import { bandAfterMove, groupMoveSteps, planLayerMove, type LayerMove, type LayerMoveTarget } from "./layer-move.ts";
import { layerOfKind } from "./layer-pick.ts";
import { panelLayerOrder, stepLayer } from "./layer-tree.ts";
import { addLayerCommand, newLayer, removeLayerCommand, removeLayersCommand, renameLayerCommand, type NewLayerKind } from "./panel-commands.ts";

/**
 * Layer authoring (ADR-025, ADR-043, ADR-047): the active layer, the layers hidden and locked by their own eye
 * and lock and by their groups', the Players band and its lock, moving, adding, renaming and removing layers,
 * each edit one command. Hidden, locked and active are view state, kept per map through `mapView`.
 */

/**
 * What layer authoring needs from the store.
 */
export interface LayerHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly mapId: () => string;
  readonly terrains: () => readonly TerrainDefinition[];
  readonly offeredTilesets: () => readonly string[];
  readonly zoneTypeCount: () => number;
  readonly terrainName: (terrainId: string, colour: number) => string;
  readonly layerTerrainName: (layerId: string) => string;
  readonly undoDepth: () => number;
  readonly refusals: () => number;
  readonly clearTileSelection: () => void;
  readonly leaveMode: (kind: LayerDocument["kind"] | undefined) => void;
  readonly allocateId: (prefix: string) => string;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
  readonly repaintAll: () => void;
}

/**
 * What callers reach of the layer groups, through `EditorStore.layers.groups`.
 */
export type GroupSlice = Pick<LayerGroups, "all" | "of" | "members" | "add" | "remove" | "setLayerGroup" | "isHidden" | "isLocked" | "setHidden" | "setLocked">;

/**
 * What callers reach of layer authoring, through `EditorStore.layers`.
 */
export type LayerSlice = Pick<LayerAuthoring, "activeId" | "setActive" | "activateOfKind" | "stepped" | "cycle" | "stepMove" | "moveStep" | "isHidden" | "isLocked" | "isEditable" | "lockedIds" | "hiddenIds" | "renameGroup" | "setHidden" | "setLocked" | "rename" | "playersAbove" | "playersLocked" | "setPlayersLocked" | "setPlayersAbove" | "move" | "moveGroup" | "add" | "canAdd" | "remove" | "removeMany"> & { readonly groups: GroupSlice };

/**
 * The layer author.
 */
export class LayerAuthoring {
  private readonly hidden = new Set<string>();
  private readonly locked = new Set<string>();
  private bandLocked = true;
  private active: string | null;

  /**
   * The map's layer groups (ADR-043): membership, their hidden and locked flags, and their edits.
   */
  public readonly groups: LayerGroups;

  /** The hidden, locked and active layers as the store keeps them per map. */
  public readonly mapView: LayerMapView;

  /**
   * @param host The store's view of itself; its map must be readable, since the first terrain layer of the map
   *   in view becomes active.
   */
  public constructor(private readonly host: LayerHost) {
    this.active = host.map().layers.find((l) => l.kind === "terrain")?.id ?? null;
    this.groups = new LayerGroups({
      map: host.map,
      run: (command) => host.run(command),
      moveLayer: (from, to) => this.move(from, to),
      notify: host.notify,
      repaint: host.repaintAll,
      allocateId: host.allocateId,
    });
    this.mapView = new LayerMapView({
      map: host.map,
      hidden: this.hidden,
      locked: this.locked,
      groups: this.groups,
      active: () => this.active,
      setActive: (layerId) => {
        this.active = layerId;
      },
    });
  }

  /**
   * The active layer's id as last set, whether or not the map in view still holds it.
   *
   * @returns The id, or null.
   */
  public get activeRaw(): string | null {
    return this.active;
  }

  /**
   * The active layer's id, or null.
   *
   * @returns The id.
   */
  public get activeId(): string | null {
    return this.active !== null && this.host.map().layers.some((l) => l.id === this.active) ? this.active : null;
  }

  /**
   * Makes a layer active. A layer of another kind changes the mode, and a selection belongs to its mode
   * (ADR-045's amendment decision 4, P1.6-T33): leaving Props empties the prop selection and leaving Markers the
   * marker selection.
   *
   * @param layerId Layer id, or null for none.
   */
  public setActive(layerId: string | null): void {
    if (layerId !== this.active) {
      this.host.clearTileSelection();
    }
    const layers = this.host.map().layers;
    const kind = layers.find((l) => l.id === layerId)?.kind;
    if (kind !== layers.find((l) => l.id === this.active)?.kind) {
      this.host.leaveMode(kind);
    }
    this.active = layerId;
    this.host.notify();
  }

  /**
   * Activates the layer of a kind that should receive edits (ADR-025): the active layer when it already
   * is one, else the topmost layer of the kind that is neither hidden nor locked. When every visible layer of
   * the kind is locked, the active one of them, else the topmost, is made active all the same, so a lock never
   * shuts a mode (ADR-128 decision 3b's ruling of 2026-10-12): the layer takes no edit until it is unlocked,
   * and the host says so on the first press.
   *
   * @param kind Wanted layer kind.
   * @returns The layer made active, or null when the map shows no layer of the kind; the active layer is then
   *   unchanged.
   */
  public activateOfKind(kind: LayerDocument["kind"]): string | null {
    const layers = this.host.map().layers;
    const chosen = layerOfKind(layers, kind, (id) => this.isEditable(id), this.active) ?? layerOfKind(layers, kind, (id) => !this.isHidden(id), this.active);
    if (chosen !== null && chosen !== this.active) {
      this.setActive(chosen);
    }
    return chosen;
  }

  /**
   * The layer `[` or `]` would make active (ADR-070 decision 10): the next visible, unlocked layer of any kind
   * from the active one, in the layers panel's order (`panelLayerOrder`), wrapping at the ends.
   *
   * @param direction Positive for up the panel (`]`), negative for down (`[`).
   * @returns The layer, or null when no layer but the active one is visible and unlocked.
   */
  public stepped(direction: number): string | null {
    const order = panelLayerOrder(this.host.map().layers, this.groups.all.map((g) => g.id), this.playersAbove);
    return stepLayer(order, this.activeId, direction, (id) => this.isEditable(id));
  }

  /**
   * Makes the stepped layer active (`stepped`); changes nothing when there is none.
   *
   * @param direction Positive for up the panel, negative for down.
   */
  public cycle(direction: number): void {
    const next = this.stepped(direction);
    if (next !== null) {
      this.setActive(next);
    }
  }

  /**
   * Plans a one-row move of a layer, a group or the Players row up or down the layers panel (`planLayerMove`),
   * which a locked or hidden layer takes too (ADR-043 amendment of 2026-09-23: reordering stays open). The
   * Players row is not offered a move while it is locked (ADR-047's amendment of 2026-10-03).
   *
   * @param target What moves.
   * @param direction Positive for up the panel, negative for down.
   * @returns The move, or null when it has nowhere to go or it is the locked Players row.
   */
  public stepMove(target: LayerMoveTarget, direction: number): LayerMove | null {
    if (target.kind === "players" && this.bandLocked) {
      return null;
    }
    return planLayerMove(this.host.map().layers, this.groups.all.map((g) => g.id), this.playersAbove, target, direction);
  }

  /**
   * Moves a layer, a group or the Players row one row up or down the layers panel (Alt+Up and Alt+Down, Layer >
   * Move layer up and Move layer down): a layer or the band as one command, a group as one `moveGroup`.
   *
   * @param target What moves.
   * @param direction Positive for up the panel, negative for down.
   * @returns Whether anything moved.
   */
  public moveStep(target: LayerMoveTarget, direction: number): boolean {
    const move = this.stepMove(target, direction);
    if (move === null) {
      return false;
    }
    const depth = this.host.undoDepth();
    if (move.kind === "layer") {
      this.move(move.from, move.to, move.ground);
    } else if (move.kind === "group") {
      this.moveGroup(move.id, move.to);
    } else {
      this.setPlayersAbove(move.index);
    }
    return this.host.undoDepth() !== depth;
  }

  /**
   * Whether a layer is hidden by its own eye or its group's.
   *
   * @param layerId Layer id.
   * @returns True when hidden.
   */
  public isHidden(layerId: string): boolean {
    return this.hidden.has(layerId) || this.groups.hidesLayer(layerId);
  }

  /**
   * Whether a layer is locked by its own lock or its group's.
   *
   * @param layerId Layer id.
   * @returns True when locked.
   */
  public isLocked(layerId: string): boolean {
    return this.locked.has(layerId) || this.groups.locksLayer(layerId);
  }

  /**
   * Whether the tools may change a layer's content: false while it is locked (ADR-043 amendment of
   * 2026-09-23) or hidden (P1.6-T9), by its own lock or eye or its group's. A layer that is not editable
   * takes no stroke, erase, placement or tile selection, and its selected props and entities are let go;
   * showing or unlocking it gives them back.
   *
   * @param layerId Layer id.
   * @returns True when neither locked nor hidden.
   */
  public isEditable(layerId: string): boolean {
    return !this.isLocked(layerId) && !this.isHidden(layerId);
  }

  /**
   * The map's layers locked by their own lock, which `isLocked` cannot tell apart from a layer its group
   * locks: what a host remembers between sessions beside the groups `groups.isLocked` names (ADR-043
   * amendment of 2026-09-24). A locked layer that undo took out of the map is left out until redo brings
   * it back.
   *
   * @returns Layer ids in document order.
   */
  public get lockedIds(): readonly string[] {
    return this.host.map().layers.filter((l) => this.locked.has(l.id)).map((l) => l.id);
  }

  /**
   * The map's layers hidden by their own eye, which `isHidden` cannot tell apart from a layer its group
   * hides: what a host remembers between sessions beside the groups `groups.isHidden` names, and what the
   * layers panel colours as the layer's own state (ADR-043 amendment of 2026-09-24, R-0013). A hidden
   * layer that undo took out of the map is left out until redo brings it back.
   *
   * @returns Layer ids in document order.
   */
  public get hiddenIds(): readonly string[] {
    return this.host.map().layers.filter((l) => this.hidden.has(l.id)).map((l) => l.id);
  }

  /**
   * Hides or shows a layer. A hidden layer takes no content edit (P1.6-T9, `isEditable`): hiding the
   * active layer drops its tile selection, and a terrain stroke in progress on it is discarded.
   *
   * @param layerId Layer id.
   * @param hidden Whether hidden.
   */
  public setHidden(layerId: string, hidden: boolean): void {
    if (hidden) {
      this.hidden.add(layerId);
    } else {
      this.hidden.delete(layerId);
    }
    this.host.notify();
    this.host.repaintAll();
  }

  /**
   * Locks or unlocks a layer. A locked layer refuses every command `layerEditedBy` names for it and takes
   * no tile selection; locking the active layer drops its selection, and locking a layer mid-stroke discards
   * the stroke.
   *
   * @param layerId Layer id.
   * @param locked Whether locked.
   */
  public setLocked(layerId: string, locked: boolean): void {
    if (locked) {
      this.locked.add(layerId);
    } else {
      this.locked.delete(layerId);
    }
    this.host.notify();
  }

  /**
   * Refuses work on a locked or hidden layer for callers that must hand back what they made.
   *
   * @param layerId Layer id.
   * @throws Error naming the layer when it is locked or hidden.
   */
  public requireEditable(layerId: string): void {
    const refusal = this.isLocked(layerId) ? "locked" : this.isHidden(layerId) ? "hidden" : null;
    if (refusal !== null) {
      throw new Error(`layer ${this.host.map().layers.find((l) => l.id === layerId)?.name ?? layerId} is ${refusal}`);
    }
  }

  /**
   * Renames a group as one command.
   *
   * @param groupId Group id.
   * @param name New name.
   * @returns False when the session refused the rename, so a field keeps what was typed; true otherwise.
   * @throws Error when the group is missing.
   */
  public renameGroup(groupId: string, name: string): boolean {
    const refusals = this.host.refusals();
    this.groups.rename(groupId, name);
    return this.host.refusals() === refusals;
  }

  /**
   * Renames a layer as one command; a same-name rename records nothing. A name typed for a terrain layer is the
   * author's (`autoName: false`), and an empty one hands the layer back to its terrain, which names it
   * (ADR-038 amendment of 2026-09-26, decision 7).
   *
   * @param layerId Layer id.
   * @param name New name.
   * @returns False when a lock or the session refused the rename, so a field keeps what was typed; true when
   *   it was recorded or there was nothing to record.
   * @throws Error when the layer is missing.
   */
  public rename(layerId: string, name: string): boolean {
    const map = this.host.map();
    const layer = map.layers.find((l) => l.id === layerId);
    const follows = layer?.kind === "terrain" && name.trim() === "";
    const command = layer?.kind === "terrain" ? renameLayerCommand(map, layerId, follows ? this.host.layerTerrainName(layerId) : name, follows) : renameLayerCommand(map, layerId, name);
    return command === null || this.host.run(command);
  }

  /**
   * The Players band (ADR-047): the number of ground layers under it, pinned or derived.
   *
   * @returns The band index.
   */
  public get playersAbove(): number {
    return playersAbove(this.host.map());
  }

  /**
   * Whether the Players row is locked (ADR-047's amendment of 2026-10-03): view state, on whenever a store
   * opens, so the panel's drag and the one-row moves never move the band by accident. It never reaches the
   * document; `setPlayersAbove` stays open to callers that set the band on purpose.
   *
   * @returns True while locked.
   */
  public get playersLocked(): boolean {
    return this.bandLocked;
  }

  /**
   * Locks or unlocks the Players row and tells subscribers.
   *
   * @param locked Whether it is locked.
   */
  public setPlayersLocked(locked: boolean): void {
    if (locked !== this.bandLocked) {
      this.bandLocked = locked;
      this.host.notify();
    }
  }

  /**
   * Pins the Players band at an index as one command.
   *
   * @param index Number of ground layers, 0 to the layer count.
   * @throws Error when the index is out of range.
   */
  public setPlayersAbove(index: number): void {
    const before = this.playersAbove;
    if (index !== before) {
      this.host.run({ kind: "setPlayersAbove", mapId: this.host.mapId(), before, after: index });
    }
  }

  /**
   * Moves a layer, re-resolving every terrain layer whose index changed. The Players band follows so the
   * other layers keep their side; the moved layer lands on the side its target index implies, or on the
   * side the caller names when the target is the band's own index (a drop right above or below the
   * Players row).
   *
   * @param from Current index.
   * @param to Resulting index.
   * @param ground Whether the layer lands under the Players band; defaults to where `to` falls.
   */
  public move(from: number, to: number, ground?: boolean): void {
    const before = this.playersAbove;
    const after = bandAfterMove(before, from, to, ground);
    if (after !== null) {
      const moved = this.host.map().layers[from];
      this.host.run({ kind: "moveLayer", mapId: this.host.mapId(), from, to, ...(moved === undefined ? {} : { layerId: moved.id }), playersAbove: { before, after } });
    }
  }

  /**
   * Moves a group's layers as one block so the block ends at a layer's slot, in their own order (ADR-043
   * amendment), gathering a split group into one block there: one command of band-aware member moves
   * (`groupMoveSteps`), planned whole first, so one undo takes it back and nothing is left half moved.
   *
   * @param groupId Group id.
   * @param to Index of the layer the block is dropped on, in the current order: the block ends above it
   *   when coming from below and takes its slot when coming from above, as a single layer would. A member's
   *   index moves nothing.
   * @param ground Whether the block lands under the Players band; defaults to where each member's slot falls.
   * @throws Error when the group is unknown or `to` is out of range.
   */
  public moveGroup(groupId: string, to: number, ground?: boolean): void {
    if (!this.groups.all.some((g) => g.id === groupId)) {
      throw new Error(`group ${groupId} is unknown`);
    }
    const layers = this.host.map().layers;
    if (!Number.isInteger(to) || to < 0 || to >= layers.length) {
      throw new Error(`index ${to} is outside 0 to ${layers.length - 1}`);
    }
    const steps = groupMoveSteps(layers, this.playersAbove, groupId, to, ground);
    if (steps === null || steps.length === 0) {
      return;
    }
    const mapId = this.host.mapId();
    const order = layers.map((l) => l.id);
    const commands: Command[] = steps.map((step) => {
      const layerId = order.splice(step.from, 1)[0] as string;
      order.splice(step.to, 0, layerId);
      return { kind: "moveLayer", mapId, from: step.from, to: step.to, layerId, playersAbove: { before: step.before, after: step.after } };
    });
    this.host.run(commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands });
  }

  /**
   * Adds an empty layer and makes it active: on top of the map, or right above the top member of the group it
   * joins, so a group's layers stay one block (ADR-043), as one command. A terrain layer takes the first corner
   * terrain the document's tilesets can use; with a blank name it follows its terrain's name (`autoName`).
   *
   * @param kind Layer kind.
   * @param name Display name; blank for a terrain layer named after its terrain.
   * @param terrainId Terrain for a terrain layer; the first corner terrain the tilesets can use by default.
   * @param group A group id to join, or `{ name }` to create a group in the same command and join it.
   * @returns The new layer's id.
   * @throws Error when a terrain layer is asked for and no usable terrain exists, or the given terrain is
   *   not in the context.
   */
  public add(kind: NewLayerKind, name: string, terrainId?: string, group?: string | { readonly name: string }): string {
    return this.insert(kind, name, terrainId, group, undefined);
  }

  /**
   * Whether `add` with its defaults can add a layer of a kind (P1.6-T69): a terrain layer needs a corner
   * terrain the document's tilesets can use, a zone layer needs the game's zone types, every other kind can.
   *
   * @param kind Layer kind.
   * @returns True when adding the kind with no terrain named succeeds.
   */
  public canAdd(kind: NewLayerKind): boolean {
    if (kind === "terrain") {
      return this.defaultTerrain() !== undefined;
    }
    return kind !== "zone" || this.host.zoneTypeCount() > 0;
  }

  /**
   * Adds an empty layer and makes it active, on top of the map or right above the top member of the group it
   * joins, as one command; a following terrain layer is named for the colour given.
   *
   * @param kind Layer kind.
   * @param name Display name; blank for a terrain layer that follows its terrain.
   * @param terrainId Terrain for a terrain layer; the first usable corner terrain by default.
   * @param group A group id to join, or `{ name }` to create one in the same command.
   * @param colour The colour a following terrain layer is named for; its terrain's first by default.
   * @returns The new layer's id.
   * @throws Error when a terrain layer is asked for and no usable terrain exists, or the given terrain is not
   *   in the context.
   */
  public insert(kind: NewLayerKind, name: string, terrainId: string | undefined, group: string | { readonly name: string } | undefined, colour: number | undefined): string {
    const terrain = terrainId === undefined ? this.defaultTerrain() : this.host.terrains().find((t) => t.id === terrainId);
    if (terrainId !== undefined && terrain === undefined) {
      throw new Error(`terrain ${terrainId} is not in the compile context`);
    }
    const id = this.host.allocateId("layer");
    const created: LayerGroup | undefined = typeof group === "object" ? { id: this.host.allocateId("group"), name: group.name } : undefined;
    const groupId = created?.id ?? (typeof group === "string" ? group : undefined);
    const follows = kind === "terrain" && terrain !== undefined && name.trim() === "";
    const map = this.host.map();
    const made = newLayer(map, kind, id, follows ? this.host.terrainName(terrain.id, colour ?? terrain.colors[0]?.index ?? 1) : name, terrain);
    const layer = follows ? { ...made, autoName: true } : made;
    const top = groupId === undefined || created !== undefined ? -1 : map.layers.map((l) => l.group).lastIndexOf(groupId);
    this.host.run(addLayerCommand(map, groupId === undefined ? layer : { ...layer, group: groupId }, created, top < 0 ? undefined : top + 1));
    this.setActive(id);
    return id;
  }

  /**
   * Removes a layer. When it was active, the layer below it (or the new top layer) becomes active. A
   * locked layer stays.
   *
   * @param layerId Layer id.
   * @throws Error when the layer is missing.
   */
  public remove(layerId: string): void {
    const index = this.host.map().layers.findIndex((l) => l.id === layerId);
    if (this.host.run(removeLayerCommand(this.host.map(), layerId)) && this.active === layerId) {
      const layers = this.host.map().layers;
      this.setActive(layers[Math.min(Math.max(0, index - 1), layers.length - 1)]?.id ?? null);
    }
  }

  /**
   * Removes several layers as one command (`removeLayersCommand`), which one undo restores in place. When the
   * active layer was among them, the lowest layer left at or under its place becomes active. A locked layer
   * among them refuses them all.
   *
   * @param layerIds The layers.
   * @returns False when a lock refused the command.
   * @throws Error when no layer is named or one is missing.
   */
  public removeMany(layerIds: readonly string[]): boolean {
    const map = this.host.map();
    const index = map.layers.findIndex((l) => l.id === this.active);
    const below = map.layers.slice(0, Math.max(0, index)).filter((l) => !layerIds.includes(l.id)).length;
    if (!this.host.run(removeLayersCommand(map, layerIds))) {
      return false;
    }
    if (this.active !== null && layerIds.includes(this.active)) {
      const layers = this.host.map().layers;
      this.setActive(layers[Math.min(Math.max(0, below - 1), layers.length - 1)]?.id ?? null);
    }
    return true;
  }

  /**
   * The terrain a new terrain layer takes when none is named: the first corner terrain on one of the tilesets
   * the editor offers (`offeredTilesetIds`). A tileset the world only pulled frames from offers no terrain
   * (ADR-129 decision 3.4), though the compile context holds its pack whole, and neither does one made whole
   * since the editor's last compile.
   *
   * @returns The terrain, or undefined when none qualifies.
   */
  private defaultTerrain(): TerrainDefinition | undefined {
    const tilesets = new Set(this.host.offeredTilesets());
    return this.host.terrains().find((t) => t.type === "corner" && tilesets.has(t.tileset));
  }
}
